import React, { useEffect, useState } from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from "react-native";
import { ArrowLeftIcon } from "@phosphor-icons/react";

import {
  ActivityProgressAnalytics,
  CourseProgressAnalytics,
  ModuleProgressAnalytics,
  ObjectiveProgressAnalytics,
  StudentDetailedAnalytics,
} from "../../../../graphql/generated/graphql";
import { formatPercentage, getSentimentColor } from "../../../../utils/analyticsUtils";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import ProgressBar from "../components/ProgressBar";
import DrillDownPanel from "../components/DrillDownPanel";
import ProgressDetailsBlock from "../components/ProgressDetailsBlock";
import { useAppConfig } from "../../../../AppConfigProvider";

const hasEngagement = (
  item: CourseProgressAnalytics | ModuleProgressAnalytics | ActivityProgressAnalytics | ObjectiveProgressAnalytics
): boolean => {
  return (
    (item.success_points && item.success_points > 0) ||
    (item.confusion_points && item.confusion_points > 0) ||
    (item.questions_asked && item.questions_asked > 0) ||
    (item.active_interaction_count && item.active_interaction_count > 0) ||
    (item.distractions && item.distractions > 0) ||
    ("completion_percentage" in item && item.completion_percentage && item.completion_percentage > 0) ||
    ("total_time_taken" in item && item.total_time_taken && item.total_time_taken > 0)
  );
};

const sortByEngagement = <
  T extends CourseProgressAnalytics | ModuleProgressAnalytics | ActivityProgressAnalytics | ObjectiveProgressAnalytics,
>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    const aActive = hasEngagement(a);
    const bActive = hasEngagement(b);
    if (aActive === bActive) return 0;
    return aActive ? -1 : 1;
  });
};

interface StudentDetailProps {
  studentDetailedAnalyticsData: StudentDetailedAnalytics | null;
  onBackToStudents: () => void;
  loading: boolean;
}

const StudentDetail: React.FC<StudentDetailProps> = ({
  studentDetailedAnalyticsData,
  onBackToStudents,
  loading: externalLoading,
}) => {
  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (studentDetailedAnalyticsData || externalLoading === false) {
      setInitializing(false);
    }
  }, [studentDetailedAnalyticsData, externalLoading]);

  if (initializing) {
    return null;
  }

  if (externalLoading === undefined && !studentDetailedAnalyticsData) {
    return null;
  }

  if (externalLoading || !studentDetailedAnalyticsData) {
    return (
      <View style={styles.loadingcontainer}>
        <ActivityIndicator />
        <CustomText text="Loading student details..." />
      </View>
    );
  }

  if (!studentDetailedAnalyticsData) {
    return (
      <View style={styles.container}>
        <TouchableOpacity style={styles.backButton} onPress={onBackToStudents}>
          <ArrowLeftIcon size={20} color="#2D2F36" />
          <CustomText text="Back to Students" />
        </TouchableOpacity>
        <CustomSpacing type="vertical" size="m" />
        <CustomText text="No analytics data found." />
      </View>
    );
  }

  // Drill-down rendering functions
  const renderObjectives = (objectives?: ObjectiveProgressAnalytics[]) => {
    if (!objectives || objectives.length === 0) {
      return <CustomText text="No objectives found." size="s" />;
    }
    const sorted = sortByEngagement(objectives);

    return (
      <View>
        {sorted.slice(0, 10).map((objective) => {
          return (
            <DrillDownPanel
              key={objective.id}
              label={objective.label}
              item={objective}
              type="objective"
              level={3}
              defaultOpen={hasEngagement(objective)} // Auto-open if has engagement
              inactive={!hasEngagement(objective)}>
              <ProgressDetailsBlock details={objective.progress_details} />
            </DrillDownPanel>
          );
        })}
      </View>
    );
  };

  const renderActivities = (activities?: ActivityProgressAnalytics[]) => {
    if (!activities || activities.length === 0) {
      return <CustomText text="No activities found." size="s" />;
    }

    const sorted = sortByEngagement(activities);
    return (
      <View>
        {sorted.slice(0, 10).map((activity) => (
          <DrillDownPanel
            key={activity.id}
            label={activity.label}
            item={activity}
            type="activity"
            level={2}
            defaultOpen={hasEngagement(activity)} // Auto-open if has engagement
            inactive={!hasEngagement(activity)}>
            {activity.details?.objectives &&
              activity.details.objectives.length > 0 &&
              renderObjectives(activity.details.objectives)}
          </DrillDownPanel>
        ))}
      </View>
    );
  };

  const renderModules = (modules?: ModuleProgressAnalytics[]) => {
    if (!modules || modules.length === 0) {
      return <CustomText text="No modules found." size="s" />;
    }

    const sorted = sortByEngagement(modules);
    return (
      <View>
        {sorted.slice(0, 10).map((module) => {
          const moduleActivities = module.details?.activities || [];

          return (
            <DrillDownPanel
              key={module.id}
              label={module.label}
              item={module}
              type="module"
              level={1}
              defaultOpen={hasEngagement(module)} // Auto-open if has engagement
              inactive={!hasEngagement(module)}>
              {moduleActivities.length > 0 ? (
                renderActivities(moduleActivities)
              ) : (
                <CustomText text="No activities found for this module." size="s" />
              )}
            </DrillDownPanel>
          );
        })}
      </View>
    );
  };

  const renderCourses = (courses?: CourseProgressAnalytics[]) => {
    if (!courses || courses.length === 0) {
      return <CustomText text="No courses found." size="s" />;
    }
    const activeCourses = courses.filter((course) => hasEngagement(course));

    if (activeCourses.length === 0) {
      return <CustomText text="No active courses found." size="s" />;
    }

    const sorted = sortByEngagement(activeCourses);
    return (
      <View>
        {sorted.map((course) => (
          <DrillDownPanel
            key={course.id}
            label={course.label}
            item={course}
            type="course"
            level={0}
            defaultOpen={true} // Always open courses by default
            inactive={false}>
            {course.details?.modules && renderModules(course.details.modules)}
          </DrillDownPanel>
        ))}
      </View>
    );
  };

  return (
    <ScrollView style={styles.container} tabIndex={0}>
      <TouchableOpacity style={styles.backButton} onPress={onBackToStudents}>
        <ArrowLeftIcon size={20} color="#2D2F36" />
        <CustomText text="Back to Students" />
      </TouchableOpacity>

      <CustomSpacing type="vertical" size="m" />

      <View style={styles.header}>
        <CustomText text={studentDetailedAnalyticsData.label} size="xl" weight="bold" useTranslationText={false} />
        <View
          style={[
            styles.sentimentBadge,
            {
              backgroundColor: getSentimentColor(studentDetailedAnalyticsData.sentiment) + "20",
              borderColor: getSentimentColor(studentDetailedAnalyticsData.sentiment),
            },
          ]}>
          <CustomText
            text={studentDetailedAnalyticsData.sentiment}
            size="s"
            style={{ color: getSentimentColor(studentDetailedAnalyticsData.sentiment), textTransform: "capitalize" }}
            useTranslationText={false}
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="m" />

      {/* Key Metrics */}
      <View style={styles.metricsGrid}>
        <View style={styles.metricCard}>
          <CustomText text="Success Points" size="s" weight="bold" style={{ color: "#10B981" }} />
          <CustomText
            text={`${studentDetailedAnalyticsData.success_points}`}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>

        <View style={styles.metricCard}>
          <CustomText text="Confusion Points" size="s" weight="bold" style={{ color: "#EF4444" }} />
          <CustomText
            text={`${studentDetailedAnalyticsData.confusion_points}`}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>

        <View style={styles.metricCard}>
          <CustomText text="Questions Asked" size="s" weight="bold" style={{ color: "#3B82F6" }} />
          <CustomText
            text={`${studentDetailedAnalyticsData.questions_asked}`}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>

        <View style={styles.metricCard}>
          <CustomText text="Total Time" size="s" weight="bold" style={{ color: "#8B5CF6" }} />
          <CustomText
            text={formatDurationFromSeconds(studentDetailedAnalyticsData.total_time_taken || 0)}
            size="xl"
            weight="bold"
            useTranslationText={false}
          />
        </View>
      </View>

      <CustomSpacing type="vertical" size="l" />

      {/* Overall Completion */}
      {studentDetailedAnalyticsData.completion_percentage !== undefined && (
        <View style={styles.completionSection}>
          <CustomText text="Overall Completion" size="l" weight="bold" />
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text={formatPercentage(
              studentDetailedAnalyticsData.completion_percentage > 1
                ? studentDetailedAnalyticsData.completion_percentage
                : studentDetailedAnalyticsData.completion_percentage * 100
            )}
            useTranslationText={false}
          />
          <ProgressBar
            percentage={
              studentDetailedAnalyticsData.completion_percentage > 1
                ? studentDetailedAnalyticsData.completion_percentage
                : studentDetailedAnalyticsData.completion_percentage * 100
            }
          />
        </View>
      )}

      <CustomSpacing type="vertical" size="l" />

      {/* Drill-Down Section */}
      <View style={styles.drillDownSection}>
        <CustomText text="Drill Down_Courses, Modules, Activities, Objectives" size="l" weight="bold" />
        <CustomText text="Click to expand each level for full analytics and progress details." size="s" />

        <CustomSpacing type="vertical" size="m" />

        {renderCourses(studentDetailedAnalyticsData.details?.courses)}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  loadingcontainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    padding: 16,
  },
  container: {
    flex: 1,
    padding: 16,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 8,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sentimentBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
  },
  metricsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: 120,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignItems: "center",
  },
  completionSection: {
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  drillDownSection: {
    marginBottom: 20,
  },
});

export default StudentDetail;
